/**
 * DocsAwaitingOcrPanel — the uploads the "Docs Awaiting OCR" tile counts, with
 * per-row Retry OCR and Dismiss (soft flag — the document is kept).
 *
 * Data Specialist WorkDesk only. Retry re-enqueues the production OCR task for a
 * stalled upload; fresh in-flight rows are not retryable (would double-process).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScanLine, Loader2 } from 'lucide-react';
import { StatusBadge } from '@/components/shared/StatusBadge';
import {
  fetchDocsAwaitingOcr,
  retryDocOcr,
  dismissDocOcr,
  type DocAwaitingOcr,
  type OcrRetryBlockedReason,
} from '@/services/docsAwaitingOcrApi';

interface DocsAwaitingOcrPanelProps {
  onChanged?: () => void;
}

const BLOCKED_TOOLTIP: Record<OcrRetryBlockedReason, string> = {
  processing: 'Still processing — retry is available 30 min after upload stalls (2 h once OCR has started)',
  image: 'Photos use the vision path — no OCR retry',
  not_retryable: 'Cannot be retried automatically — re-upload the document',
  retry_limit: 'Retry limit reached — re-upload the document',
};

function formatAge(minutes: number | null): string {
  if (minutes === null) return '—';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 48) return `${Math.floor(minutes / 60)}h`;
  return `${Math.floor(minutes / 1440)}d`;
}

export function DocsAwaitingOcrPanel({ onChanged }: DocsAwaitingOcrPanelProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<DocAwaitingOcr[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetchDocsAwaitingOcr();
      setItems(res.items);
      setTotal(res.total);
      setLoadError(false);
    } catch {
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (id: string, action: () => Promise<unknown>, removeOnSuccess: boolean) => {
    setPending((p) => ({ ...p, [id]: true }));
    setRowError((e) => {
      const { [id]: _drop, ...rest } = e;
      return rest;
    });
    try {
      await action();
      // Never remove before the 2xx.
      if (removeOnSuccess) {
        setItems((prev) => prev.filter((i) => i.uploadId !== id));
        setTotal((t) => Math.max(0, t - 1));
      } else {
        await load();
      }
      setConfirming(null);
      onChanged?.();
    } catch (err: any) {
      setRowError((e) => ({ ...e, [id]: err?.message || 'Action failed' }));
    } finally {
      setPending((p) => ({ ...p, [id]: false }));
    }
  };

  return (
    <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
      <div className="flex items-center gap-2 mb-4">
        <ScanLine className="w-4 h-4 text-[var(--color-text-secondary)]" />
        <h2 className="font-serif text-lg text-[var(--color-text-primary)]">Docs Awaiting OCR</h2>
        <span className="font-mono text-sm text-[var(--color-text-muted)]">{total}</span>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-9 rounded-md bg-[var(--color-bg-base)] animate-pulse" />
          ))}
        </div>
      ) : loadError ? (
        <p className="text-sm text-[var(--color-text-muted)]">Could not load documents awaiting OCR.</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">No documents awaiting OCR</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-[var(--color-hairline)]">
              <th className="py-2 pr-3 cs-label">Filename</th>
              <th className="py-2 pr-3 cs-label">Case</th>
              <th className="py-2 pr-3 cs-label">Status</th>
              <th className="py-2 pr-3 cs-label text-right">Age</th>
              <th className="py-2 pr-3 cs-label">Reason</th>
              <th className="py-2 cs-label text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((doc) => {
              const busy = !!pending[doc.uploadId];
              const blocked = doc.retryBlockedReason ? BLOCKED_TOOLTIP[doc.retryBlockedReason] : undefined;
              return (
                <tr key={doc.uploadId} className="border-b border-[var(--color-hairline)] last:border-0 align-top">
                  <td className="py-2 pr-3 max-w-[260px]">
                    <div className="truncate text-[var(--color-text-primary)]" title={doc.filename}>
                      {doc.filename}
                    </div>
                    {rowError[doc.uploadId] && (
                      <div role="alert" className="mt-1 text-xs text-[var(--color-blocker)]">
                        {rowError[doc.uploadId]}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {doc.caseId ? (
                      <button
                        type="button"
                        className="text-[var(--color-text-primary)] hover:underline"
                        onClick={() => navigate(`/data-specialist/case/${doc.caseId}/documents`)}
                      >
                        {doc.clientName || <span className="text-[var(--color-text-muted)]">Unassigned</span>}
                      </button>
                    ) : (
                      <span className="text-[var(--color-text-muted)]">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <StatusBadge
                      text={doc.stalled ? 'Stalled' : doc.status === 'processing' ? 'Processing' : 'Uploaded'}
                      variant={doc.stalled ? 'warning' : 'neutral'}
                      size="sm"
                    />
                  </td>
                  <td className="py-2 pr-3 text-right font-mono text-[var(--color-text-secondary)]">
                    {formatAge(doc.ageMinutes)}
                  </td>
                  <td className="py-2 pr-3 max-w-[260px]">
                    <div
                      className="line-clamp-1 text-xs text-[var(--color-text-muted)]"
                      title={doc.suggestedRemediation || undefined}
                    >
                      {doc.suggestedRemediation || '—'}
                    </div>
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {confirming === doc.uploadId ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-xs text-[var(--color-text-secondary)]">
                          Dismiss from queue? The document is kept.
                        </span>
                        <button
                          type="button"
                          disabled={busy}
                          className="px-2 py-1 text-xs rounded-md border border-[var(--color-border)] text-[var(--color-text-primary)] disabled:opacity-50"
                          onClick={() => run(doc.uploadId, () => dismissDocOcr(doc.uploadId), true)}
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          className="px-2 py-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                          onClick={() => setConfirming(null)}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        {blocked && (
                          <span className="text-xs text-[var(--color-text-muted)] whitespace-normal max-w-[200px] text-left">
                            {blocked}
                          </span>
                        )}
                        {doc.retryBlockedReason !== 'image' && (
                          <span title={blocked}>
                            <button
                              type="button"
                              disabled={!doc.retryable || busy}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-[var(--color-border)] text-[var(--color-text-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => run(doc.uploadId, () => retryDocOcr(doc.uploadId), false)}
                            >
                              {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                              Retry OCR
                            </button>
                          </span>
                        )}
                        <button
                          type="button"
                          disabled={busy}
                          className="px-2 py-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] disabled:opacity-50"
                          onClick={() => setConfirming(doc.uploadId)}
                        >
                          Dismiss
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default DocsAwaitingOcrPanel;
